import  { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { useLocation } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import BookingInfoModal from '../components/BookingInfoModal';
import DaySummaryModal from '../components/DaySummaryModal';
import CreateMobileBookingModal from '../components/CreateMobileBookingModal';
import '../components/MobileListView.css';
// Removed all persistence systems
import { CalendarBooking, getCalendarBookings, getBooking } from '../lib/bookings';
import { getBookingFeedback } from '../lib/feedback';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import PortalDropdown from '../components/ui/PortalDropdown';
import { useLongPress } from '../hooks/useLongPress';
import BookingQuickInfoModal from '../components/BookingQuickInfoModal';
import { useSimpleTimer } from '../contexts/SimpleTimerContext';


interface BookingPayload {
  id: string;
  business_id: string;
  [key: string]: any;
}

// Cache for basic blocks
const basicBlocksCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache for full details
const detailsCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache for time off requests
const timeOffCache = new Map<string, { 
  data: CalendarBooking[], 
  timestamp: number 
}>();

// Cache duration in milliseconds (15 minutes)
const CACHE_DURATION = 15 * 60 * 1000;

export default function CalendarV2() {
  const { selectedBusiness, loading: businessLoading } = useBusiness();
  const { canAccess, currentResource } = usePermissionCheck();
  const { activeTimer, getElapsedSeconds } = useSimpleTimer();
  const location = useLocation();

  // Check for navigation state immediately to determine initial modal state
  const navigationState = location.state as { selectedBookingId?: string } | null;
  const shouldOpenBookingModal = Boolean(navigationState?.selectedBookingId);

  const [currentDate, setCurrentDate] = useState(new Date());
  const [calendarTitle, setCalendarTitle] = useState('');
  const [bookings, setBookings] = useState<CalendarBooking[]>([]);
  const [timeOffRequests, setTimeOffRequests] = useState<CalendarBooking[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [viewStartDate, setViewStartDate] = useState<Date>(new Date());
  const [viewEndDate, setViewEndDate] = useState<Date>(new Date());
  const [loadedBookingIds, setLoadedBookingIds] = useState<Set<string>>(new Set());
  const [selectedBooking, setSelectedBooking] = useState<any>(null);
  const [isBookingInfoModalOpen, setIsBookingInfoModalOpen] = useState(shouldOpenBookingModal); // Open immediately if needed
  const [isLoadingBookingFromNav, setIsLoadingBookingFromNav] = useState(shouldOpenBookingModal);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [isDaySummaryOpen, setIsDaySummaryOpen] = useState(false);
  const [isBookingQuickInfoModalOpen, setIsBookingQuickInfoModalOpen] = useState(false);
  const [quickInfoBooking, setQuickInfoBooking] = useState<any>(null);
  const [timerElapsedTime, setTimerElapsedTime] = useState(0);
  const [daySummaryPosition, setDaySummaryPosition] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [totalSales, setTotalSales] = useState<number>(0);
  const [isCalculatingSales, setIsCalculatingSales] = useState(true);
  const [isRescheduleMobileBookingModalOpen, setIsRescheduleMobileBookingModalOpen] = useState(false);
  const [bookingToReschedule, setBookingToReschedule] = useState<any | null>(null);
  const [salesInitiallyLoaded, setSalesInitiallyLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // Remove local sidebar state - we'll use CSS variables instead
  
  // Reference to the calendar instance
  const calendarRef = useRef<FullCalendar>(null);
  const isMounted = useRef(true);
  
  // Add reference to track fetch calls to prevent multiple simultaneous fetches
  const isFetching = useRef(false);
  const fetchTimeout = useRef<NodeJS.Timeout | null>(null);
  const hasInitialScrolled = useRef(false);
  const [calendarLoaded, setCalendarLoaded] = useState(false);
  const [isCalendarReady, setIsCalendarReady] = useState(false);

  // Add feedback state
  const [bookingFeedback, setBookingFeedback] = useState<Map<string, { rating: number; hasText: boolean }>>(new Map());

  // Modal persistence hook
  // Removed all persistence systems

  // Removed all persistence systems

  // Get cache key based on business ID and date range
  const getCacheKey = useCallback((businessId: string, startDate: Date, endDate: Date) => {
    return `${businessId}_${startDate.toISOString()}_${endDate.toISOString()}`;
  }, []);

  // Memoize the currentMonthStart and currentMonthEnd
  const dateRange = useMemo(() => {
    const currentMonthStart = new Date(viewStartDate);
    currentMonthStart.setDate(1);
    currentMonthStart.setHours(0, 0, 0, 0);
    
    const currentMonthEnd = new Date(currentMonthStart);
    currentMonthEnd.setMonth(currentMonthEnd.getMonth() + 1);
    currentMonthEnd.setDate(0);
    currentMonthEnd.setHours(23, 59, 59, 999);
    
    return { currentMonthStart, currentMonthEnd };
  }, [viewStartDate]);
  
  // Function to fetch approved time off requests
  const fetchTimeOffRequests = useCallback(async (startDate: Date, endDate: Date) => {
    if (!selectedBusiness?.id || !supabase) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('time_off_requests')
        .select(`
          id,
          start_date,
          end_date,
          request_type,
          is_partial_day,
          start_time,
          end_time,
          resource_id,
          resources:resource_id (
            id,
            first_name,
            last_name
          )
        `)
        .eq('business_id', selectedBusiness.id)
        .eq('status', 'approved')
        .lte('start_date', format(endDate, 'yyyy-MM-dd'))
        .gte('end_date', format(startDate, 'yyyy-MM-dd'));

      if (error) {
        console.error('Error fetching time off requests:', error);
        return [];
      }

      // Transform time off requests into CalendarBooking format
      const timeOffEvents: CalendarBooking[] = (data || []).map((request) => {
        const resource = Array.isArray(request.resources) ? request.resources[0] : request.resources;
        const resourceName = resource 
          ? `${resource.first_name} ${resource.last_name}`
          : 'Unknown Resource';

        const typeLabels = {
          vacation: 'Vacation',
          sick: 'Sick Leave',
          personal: 'Personal Day',
          family: 'Family Emergency',
          bereavement: 'Bereavement',
          other: 'Other'
        };

        const typeLabel = typeLabels[request.request_type as keyof typeof typeLabels] || request.request_type;

        const eventTitle = `${typeLabel} - ${resourceName}`;
        
        // For all-day events, FullCalendar expects the end date to be exclusive (day after the last day)
        let endDate = request.end_date;
        if (!request.is_partial_day) {
          // Parse as local date to avoid timezone issues
          const [year, month, day] = request.end_date.split('-').map(Number);
          const localEndDate = new Date(year, month - 1, day);
          const nextDay = addDays(localEndDate, 1);
          endDate = format(nextDay, 'yyyy-MM-dd');
        }

        return {
          id: `timeoff_${request.id}`,
          title: eventTitle,
          booking_number: 0, // Not applicable for time off
          start: request.is_partial_day && request.start_time 
            ? `${request.start_date}T${request.start_time}:00`
            : request.start_date,
          end: request.is_partial_day && request.end_time
            ? `${request.end_date}T${request.end_time}:00`
            : endDate,
          backgroundColor: '#000000', // Black color for time off
          borderColor: '#000000',
          textColor: '#ffffff', // White text on black background
          allDay: !request.is_partial_day,
          display: 'block',
          classNames: ['time-off-event'],
          extendedProps: {
            customer: resourceName,
            status: 'Time Off',
            booking_number: 0,
            subtotal: 0,
            total_amount: 0,
            is_time_off: true,
            time_off_type: typeLabel,
            resource_name: resourceName
          }
        };
      });

      return timeOffEvents;
    } catch (error) {
      console.error('Error fetching time off requests:', error);
      return [];
    }
  }, [selectedBusiness?.id]);

  // Optimized fetch with immediate rendering - no more two-phase loading
  const fetchBookings = useCallback(async (forceFetch = false) => {
    if (!selectedBusiness?.id) return;
    if (isFetching.current) return;
    
    if (fetchTimeout.current) {
      clearTimeout(fetchTimeout.current);
    }

    try {
      setIsLoading(true);
      setError(null);
      isFetching.current = true;
      
      const { currentMonthStart, currentMonthEnd } = dateRange;
      
      // Determine if we should filter by resource based on the viewAllBookings permission
      const shouldFilterByResource = !canAccess.viewAllBookings && currentResource?.id;
      const filterResourceId = shouldFilterByResource ? currentResource.id : undefined;
      
      // Include resource filtering in cache key if filtering is active
      const cacheKey = getCacheKey(
        selectedBusiness.id, 
        currentMonthStart, 
        currentMonthEnd
      ) + (filterResourceId ? `_resource_${filterResourceId}` : '');
      
      // Check cache first for both bookings and time off requests
      const cachedBookings = basicBlocksCache.get(cacheKey);
      const cachedTimeOff = timeOffCache.get(cacheKey);
      const now = Date.now();
      
      let bookings: CalendarBooking[];
      let timeOffEvents: CalendarBooking[];
      
      if (!forceFetch && cachedBookings && cachedTimeOff && 
          (now - cachedBookings.timestamp) < CACHE_DURATION &&
          (now - cachedTimeOff.timestamp) < CACHE_DURATION) {
        // Use cached data for both bookings and time off
        bookings = cachedBookings.data;
        timeOffEvents = cachedTimeOff.data;
        console.log(`Using cached data: ${bookings.length} bookings, ${timeOffEvents.length} time off requests`);
      } else {
        // Fetch both bookings and time off requests simultaneously
        const [fetchedBookings, fetchedTimeOff] = await Promise.all([
          getCalendarBookings(
            selectedBusiness.id,
            currentMonthStart,
            currentMonthEnd,
            true, // isLocationBooking = true for CalendarV2
            filterResourceId // Pass the resource ID for filtering if needed
          ),
          fetchTimeOffRequests(currentMonthStart, currentMonthEnd)
        ]);
        
        bookings = fetchedBookings;
        timeOffEvents = fetchedTimeOff;
        
        // Update both caches
        basicBlocksCache.set(cacheKey, { data: bookings, timestamp: now });
        timeOffCache.set(cacheKey, { data: timeOffEvents, timestamp: now });
        
        console.log(`Fetched ${bookings.length} location bookings${shouldFilterByResource ? ` filtered for resource ${currentResource.first_name} ${currentResource.last_name}` : ' (all bookings)'} and ${timeOffEvents.length} time off requests`);
      }

      // Check for active timers for this resource
      if (currentResource?.id) {
        try {
          const { data: activeTimerLogs } = await supabase
            .from('booking_timer_logs')
            .select('booking_id')
            .eq('resource_id', currentResource.id)
            .eq('is_running', true);
          
          if (activeTimerLogs && activeTimerLogs.length > 0) {
            console.log('🔥 Found active timer for booking:', activeTimerLogs[0].booking_id);
            // The timer context will handle displaying the timer on the correct booking
          }
        } catch (error) {
          console.error('Error checking for active timers:', error);
        }
      }

      // Update the calendar immediately with complete visual data
      if (isMounted.current) {
        setBookings(bookings);
        setTimeOffRequests(timeOffEvents);
        setLoadedBookingIds(new Set(bookings.map(b => b.id)));
        
        // Load feedback for completed bookings
        loadFeedbackForBookings(bookings);
        
        setTimeout(() => {
          if (isMounted.current) {
            setIsLoading(false);
            setIsCalendarReady(true); // Mark calendar as ready after normal loading
          }
        }, 200); // Small delay to ensure everything is fully rendered
      }
    } catch (err) {
      console.error('Error fetching bookings:', err);
      if (isMounted.current) {
        setError('Failed to load bookings. Please try refreshing the page.');
      }
    } finally {
      if (isMounted.current) {
        setIsLoading(false);
      }
      isFetching.current = false;
    }
  }, [selectedBusiness?.id, dateRange, getCacheKey, canAccess.viewAllBookings, currentResource?.id, fetchTimeOffRequests]);
  
  // Calculate total sales for the current month view
  const calculateTotalSales = useCallback(async () => {
    if (!selectedBusiness?.id || !supabase) return;
    setIsCalculatingSales(true);
    
    try {
      // Get the exact month and year being displayed in the calendar view
      const calendarYear = currentDate.getFullYear();
      const calendarMonth = currentDate.getMonth(); // 0-based month (0 = January)
      
      // Calculate first and last day of the displayed month
      const monthStart = new Date(calendarYear, calendarMonth, 1);
      const monthEnd = new Date(calendarYear, calendarMonth + 1, 0);
      
      // Format to YYYY-MM-DD
      const formattedMonthStart = monthStart.toISOString().split('T')[0];
      const formattedMonthEnd = monthEnd.toISOString().split('T')[0];
      
      console.log('Calculating sales for month:', {
        currentMonth: `${monthStart.toLocaleString('default', { month: 'long' })} ${calendarYear}`,
        dateRange: `${formattedMonthStart} to ${formattedMonthEnd}`
      });
      
      // Fetch bookings that END within this exact month range, excluding mobile bookings
      const { data: salesData, error: salesError } = await supabase
        .from('bookings')
        .select('id, booking_number, total_amount, tax_amount, subtotal, discount_amount, end_date, mobile_booking')
        .eq('business_id', selectedBusiness.id)
        .eq('mobile_booking', false) // Only include location bookings (non-mobile)
        .eq('is_deleted', false) // Exclude deleted bookings
        .gte('end_date', formattedMonthStart)
        .lte('end_date', formattedMonthEnd);
      
      if (salesError) {
        console.error('Error fetching sales data:', salesError);
        return;
      }
      
      if (salesData && salesData.length > 0) {
        // Log bookings included in the calculation 
        console.log('Location bookings included in current month sales calculation:', 
          salesData.map(b => ({ 
            id: b.id,
            booking_number: b.booking_number,
            end_date: b.end_date, 
            subtotal: b.subtotal,
            discount_amount: b.discount_amount,
            tax_amount: b.tax_amount,
            total_amount: b.total_amount,
            mobile_booking: b.mobile_booking,
            sales_amount: (b.subtotal || 0) - (b.discount_amount || 0)
          }))
        );
        
        // Sum up all sales amounts (after discounts, excluding tax)
        const sum = salesData.reduce((acc, booking) => {
          // Calculate discounted sales amount: subtotal - discount_amount
          const subtotal = booking.subtotal || 0;
          const discountAmount = booking.discount_amount || 0;
          const discountedSalesAmount = subtotal - discountAmount;
          
          return acc + discountedSalesAmount;
        }, 0);
        
        if (isMounted.current) {
          setTotalSales(sum);
          setSalesInitiallyLoaded(true);
        }
      } else {
        console.log(`No location bookings with end dates in ${monthStart.toLocaleString('default', { month: 'long' })} ${calendarYear}`);
        if (isMounted.current) {
          setTotalSales(0);
          setSalesInitiallyLoaded(true);
        }
      }
    } catch (err) {
      console.error('Error calculating sales:', err);
      if (isMounted.current) {
        setSalesInitiallyLoaded(true);
      }
    } finally {
      if (isMounted.current) {
        setIsCalculatingSales(false);
      }
    }
  }, [selectedBusiness?.id, currentDate]);
  
  // Update timer display
  useEffect(() => {
    console.log('🔥 CalendarV2 activeTimer state changed:', activeTimer);
    if (!activeTimer) {
      setTimerElapsedTime(0);
      return;
    }

    const updateTime = () => {
      setTimerElapsedTime(getElapsedSeconds());
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, [activeTimer, getElapsedSeconds]);

  // Defer sales calculation until after calendar loads to improve initial performance
  useEffect(() => {
    if (selectedBusiness?.id && calendarRef.current && bookings.length > 0) {
      // Defer sales calculation by 300ms to not block calendar rendering
      setTimeout(() => {
        calculateTotalSales();
      }, 300);
    }
  }, [calculateTotalSales, selectedBusiness?.id, currentDate, bookings.length]);

  // Check for preloaded calendar data and use it if available
  const usePreloadedCalendarData = useCallback(() => {
    try {
      const preloadedDataStr = localStorage.getItem('preloadedCalendarData');
      if (!preloadedDataStr) {
        console.log('📅 No preloaded calendar data found');
        return false;
      }

      const preloadedData = JSON.parse(preloadedDataStr);
      
      // Check if data is still fresh (within 15 minutes)
      const dataAge = Date.now() - preloadedData.timestamp;
      if (dataAge > CACHE_DURATION) {
        console.log('📅 Preloaded calendar data is stale, removing');
        localStorage.removeItem('preloadedCalendarData');
        return false;
      }

      console.log('📅 Using preloaded calendar data - instant calendar loading!');
      
      // Set all the calendar state immediately with preloaded data
      setBookings(preloadedData.bookings || []);
      setLoadedBookingIds(new Set((preloadedData.bookings || []).map((b: any) => b.id)));
      setTotalSales(preloadedData.totalSales || 0);
      setSalesInitiallyLoaded(true);
      setIsCalculatingSales(false);
      
      // Restore booking feedback
      if (preloadedData.bookingFeedback && Array.isArray(preloadedData.bookingFeedback)) {
        const feedbackMap = new Map<string, { rating: number; hasText: boolean }>(
          preloadedData.bookingFeedback as [string, { rating: number; hasText: boolean }][]
        );
        setBookingFeedback(feedbackMap);
      }
      
      // Set initial date for scroll positioning
      if (preloadedData.initialDate) {
        const initialDate = new Date(preloadedData.initialDate);
        setCurrentDate(initialDate);
      }
      
      // Clear loading states immediately
      setIsLoading(false);
      
      // Mark calendar as ready since we have instant data
      setIsCalendarReady(true);
      
      // Clean up the preloaded data since we've used it
      localStorage.removeItem('preloadedCalendarData');
      
      console.log('📅 Calendar state set instantly from preloaded data:', {
        bookingsCount: preloadedData.bookings?.length || 0,
        totalSales: preloadedData.totalSales || 0,
        feedbackCount: preloadedData.bookingFeedback?.length || 0
      });
      
      return true;
    } catch (error) {
      console.error('📅 Error using preloaded calendar data:', error);
      localStorage.removeItem('preloadedCalendarData');
      return false;
    }
  }, []);

  // Initialize on component mount
  useEffect(() => {
    isMounted.current = true;
    
    // Wait for business context to be loaded
    if (businessLoading) {
      console.log('📅 CalendarV2: Waiting for business context to load...');
      return;
    }
    
    if (selectedBusiness?.id) {
      // First try to use preloaded calendar data for instant loading
      const usedPreloadedData = usePreloadedCalendarData();
      
      if (!usedPreloadedData) {
        // Only fetch bookings if we didn't use preloaded data
        console.log('📅 No preloaded data available, fetching bookings normally');
        fetchBookings();
      }
    } else {
      console.log('📅 CalendarV2: No selected business available yet');
    }
    
    return () => {
      isMounted.current = false;
      if (fetchTimeout.current) {
        clearTimeout(fetchTimeout.current);
      }
    };
  }, [fetchBookings, selectedBusiness?.id, businessLoading, usePreloadedCalendarData]);
  
  // Update the calendar title whenever the current date changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      setCalendarTitle(calendarApi.view.title);
    }
  }, [currentDate]);

  // Handle navigation state to load booking data for the already-opened modal
  useEffect(() => {
    const state = navigationState as { selectedBookingId?: string; skipModalOpen?: boolean } | null;
    
    // If skipModalOpen is true, don't open the modal (it's already open via portal)
    if (state?.skipModalOpen) {
      console.log('🚀 Skipping modal open - already opened via portal');
      window.history.replaceState({}, document.title);
      return;
    }
    
    if (state?.selectedBookingId && isLoadingBookingFromNav) {
      const loadBookingData = async () => {
        try {
          console.log('🚀 Loading booking data for already-opened modal, ID:', state.selectedBookingId);
          
          // Fetch the specific booking details
          const booking = await getBooking(state.selectedBookingId!);
          setSelectedBooking(booking);
          
          // Clear the navigation state to prevent reopening on refresh
          window.history.replaceState({}, document.title);
          
          console.log('✅ Booking data loaded successfully');
        } catch (err) {
          console.error('Error loading booking from navigation state:', err);
          setError('Failed to load booking details. Please try again.');
          // Close the modal if we can't load the booking
          setIsBookingInfoModalOpen(false);
        } finally {
          setIsLoadingBookingFromNav(false);
        }
      };
      
      // Execute immediately
      loadBookingData();
    }
  }, []); // Run only once on mount
  
  // Set up Supabase real-time subscription for bookings
  useEffect(() => {
    if (!supabase || !selectedBusiness?.id) return;
    
    console.log('Setting up real-time subscription for bookings');
    
    // Subscribe to changes in the bookings table
    const subscription = supabase
      .channel('bookings-changes')
      .on('postgres_changes', 
        { 
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public', 
          table: 'bookings',
          filter: `business_id=eq.${selectedBusiness.id}`
        }, 
        async (payload: any) => {
          const bookingId = payload.new?.id || payload.old?.id;
          console.log('Received booking change:', {
            event: payload.eventType,
            bookingId,
            timestamp: new Date().toISOString()
          });

          // Handle different types of changes
          switch (payload.eventType) {
            case 'INSERT':
              console.log('New booking created:', payload.new);
              // Fetch the new booking details
              await fetchBookings(true);
              break;
              
            case 'UPDATE':
              console.log('Booking updated:', {
                old: payload.old,
                new: payload.new
              });
              // Refresh the bookings to get updated data
              await fetchBookings(true);
              break;
              
            case 'DELETE':
              console.log('Booking deleted:', payload.old);
              // Remove the deleted booking from the state
              if (payload.old?.id) {
                setBookings(prevBookings => 
                  prevBookings.filter(booking => booking.id !== payload.old.id)
                );
              }
              break;
          }
        }
      )
      .subscribe((status) => {
        console.log('Subscription status:', status);
      });
    
    // Cleanup subscription on unmount
    return () => {
      console.log('Cleaning up real-time subscription');
      subscription.unsubscribe();
    };
  }, [selectedBusiness?.id, fetchBookings]);

  // Listen for immediate booking creation events from CreateBookingModal
  useEffect(() => {
    const handleBookingCreated = (event: any) => {
      const { bookingId, businessId } = event.detail;
      console.log('📅 Received immediate bookingCreated event:', { bookingId, businessId });
      
      // Only refresh if the booking is for the current business
      if (businessId === selectedBusiness?.id) {
        console.log('📅 Refreshing calendar immediately for new booking');
        fetchBookings(true); // Force fetch to get the new booking
      }
    };

    // Add event listener for immediate updates
    window.addEventListener('bookingCreated', handleBookingCreated);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('bookingCreated', handleBookingCreated);
    };
  }, [selectedBusiness?.id, fetchBookings]);

  // Listen for Nexus booking modification events
  useEffect(() => {
    const handleNexusBookingUpdate = (event: any) => {
      const { businessId, modifications } = event.detail;
      console.log('🤖 Received Nexus booking update event:', { businessId, modifications });
      
      // Only refresh if the booking modification is for the current business
      if (businessId === selectedBusiness?.id) {
        console.log('🤖 Refreshing calendar immediately for Nexus booking modifications');
        fetchBookings(true); // Force fetch to get the updated bookings
      }
    };

    // Add event listener for Nexus booking updates
    window.addEventListener('nexusBookingUpdate', handleNexusBookingUpdate);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('nexusBookingUpdate', handleNexusBookingUpdate);
    };
  }, [selectedBusiness?.id, fetchBookings]);
  
  // Handle date changes (when calendar navigates)
  const handleDatesSet = useCallback((arg: any) => {
    setCurrentDate(arg.view.currentStart);
    setCalendarTitle(arg.view.title);
    setViewStartDate(arg.start);
    setViewEndDate(arg.end);
  }, []);
  
  // Optimized navigation handlers with faster transitions
  const handlePrev = useCallback(() => {
    if (calendarRef.current) {
      // Hide header during transition for smoother experience
      setIsCalendarReady(false);
      
      // Much faster navigation with minimal delay
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.prev();
          setCurrentDate(calendarApi.getDate());
          
          // Immediate fetch without nested timeouts
          fetchBookings(true);
        }
      }, 150); // Reduced from 300ms to 150ms
    }
  }, [fetchBookings]);
  
  const handleNext = useCallback(() => {
    if (calendarRef.current) {
      // Hide header during transition for smoother experience
      setIsCalendarReady(false);
      
      // Much faster navigation with minimal delay
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.next();
          setCurrentDate(calendarApi.getDate());
          
          // Immediate fetch without nested timeouts
          fetchBookings(true);
        }
      }, 150); // Reduced from 300ms to 150ms
    }
  }, [fetchBookings]);
  
  const handleToday = useCallback(() => {
    if (calendarRef.current) {
      // Hide header during transition for smoother experience
      setIsCalendarReady(false);
      
      // Much faster navigation with minimal delay
      setTimeout(() => {
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.today();
          setCurrentDate(calendarApi.getDate());
          
          // Immediate fetch without nested timeouts
          fetchBookings(true);
        }
      }, 150); // Reduced from 300ms to 150ms
    }
  }, [fetchBookings]);
  
  // Handle event click
  const handleEventClick = useCallback(async (info: any) => {
    try {
      setIsLoading(true);
      setSelectedBooking(null); // Clear any previous booking
      // Fetch full booking details first
      const booking = await getBooking(info.event.id);
      setSelectedBooking(booking);
      setIsBookingInfoModalOpen(true); // Open modal only after booking is loaded
    } catch (err) {
      setError('Failed to load booking details. Please try again.');
      setIsBookingInfoModalOpen(false);
    } finally {
      setIsLoading(false);
    }
    }, []);

  // Handle long press on event
  const handleLongPress = useCallback(async (bookingId: string) => {
    try {
      // Fetch booking details for the quick info modal
      const booking = await getBooking(bookingId);
      
      setQuickInfoBooking(booking);
      setIsBookingQuickInfoModalOpen(true);
    } catch (error) {
      console.error('Error loading booking for quick info:', error);
      setError('Failed to load booking details');
    }
  }, []);



  // Handler for rescheduling mobile bookings
  const handleRescheduleMobileBooking = useCallback(async () => {
    if (selectedBooking && selectedBooking.extendedProps?.mobile_booking) {
      try {
        // Fetch the full booking data before opening reschedule modal
        const fullBooking = await getBooking(selectedBooking.id);
        if (fullBooking) {
          setBookingToReschedule(fullBooking);
          setIsRescheduleMobileBookingModalOpen(true);
          setIsBookingInfoModalOpen(false);
        }
      } catch (error) {
        console.error('Error fetching booking for reschedule:', error);
      }
    }
  }, [selectedBooking]);

  // Memoize the event content renderer to show loading state
  const renderEventContent = useCallback((eventInfo: any) => {
    // Time off events are always "loaded" since they don't need additional data fetching
    const isTimeOffEvent = eventInfo.event.id.startsWith('timeoff_');
    const isLoaded = isTimeOffEvent || loadedBookingIds.has(eventInfo.event.id);
    
    // Handle time off events with simpler rendering
    if (isTimeOffEvent) {
      return (
        <div className="relative h-full w-full p-1 overflow-hidden">
          <div className="text-[9px] sm:text-xs font-medium text-white relative z-10">
            {eventInfo.event.title}
          </div>
          <div className="text-[8px] sm:text-xs opacity-75 text-white relative z-10 mt-0.5">
            {eventInfo.event.extendedProps.time_off_type}
          </div>
        </div>
      );
    }
    // Prefer drop_off_time, fallback to start_time
    const dropOffTime = eventInfo.event.extendedProps.drop_off_time;
    const startTime = eventInfo.event.extendedProps.start_time;
    const timeToShow = dropOffTime || startTime;
    const bookingNumber = eventInfo.event.extendedProps.booking_number;
    // Get secondary color from both possible locations
    const secondaryColor = eventInfo.event.extendedProps.secondary_color || 
                          eventInfo.event.extendedProps.secondaryColor || 
                          eventInfo.event.secondaryColor;
    
    // Get booking status to determine if we should show secondary color
    const bookingStatus = eventInfo.event.extendedProps.status;
    const shouldShowSecondaryColor = secondaryColor && 
                                   bookingStatus !== 'in_progress' && 
                                   bookingStatus !== 'completed' && 
                                   bookingStatus !== 'cancelled';
    
    // Get feedback data for this booking
    const feedbackData = bookingFeedback.get(eventInfo.event.id);
    const showFeedbackRating = bookingStatus === 'completed' && feedbackData;
    
    // Calculate workflow progress for in-progress bookings
    let totalSteps = 0;
    let completedSteps = 0;
    let showWorkflowProgress = false;
    
    if (bookingStatus === 'in_progress' && eventInfo.event.extendedProps.services) {
      // Check if any service has workflow steps
      const services = eventInfo.event.extendedProps.services;
      for (const service of services) {
        if (service.workflow_steps && Array.isArray(service.workflow_steps)) {
          service.workflow_steps.forEach((step: any) => {
            totalSteps++;
            if (step.status === 'completed') {
              completedSteps++;
            }
          });
        }
      }
      showWorkflowProgress = totalSteps > 0;
    }

    // Check if this booking has an active timer
    const hasActiveTimer = activeTimer?.bookingId === eventInfo.event.id;
    
    // Debug logging for timer detection
    if (hasActiveTimer) {
      console.log('🔥 Active timer detected for booking:', eventInfo.event.id, 'Timer:', activeTimer);
    }
    
    // Format timer display
    const formatTimerDisplay = (seconds: number) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      if (hours > 0) {
        return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
      } else {
        return `${minutes}:${secs.toString().padStart(2, '0')}`;
      }
    };
    
    // Calculate payment status
    const totalAmount = eventInfo.event.extendedProps.total_amount || 0;
    const payments = eventInfo.event.extendedProps.payments || [];
    const totalPaid = payments
      .filter((p: any) => p.status === 'completed')
      .reduce((sum: number, p: any) => sum + p.amount, 0);

    let paymentStatus = '';
    let paymentStampColor = '';
    
    if (totalAmount > 0) {
      if (totalPaid === 0) {
        paymentStatus = 'UNPAID';
        paymentStampColor = 'text-white';
      } else if (totalPaid >= totalAmount) {
        paymentStatus = 'PAID';
        paymentStampColor = 'text-white';
      } else {
        paymentStatus = 'PARTIAL';
        paymentStampColor = 'text-white';
      }
    }
    
    let formattedTime = '';
    if (timeToShow) {
      // Format as HH:MM AM/PM
      const [h, m] = timeToShow.split(':');
      const date = new Date();
      date.setHours(Number(h), Number(m), 0, 0);
      formattedTime = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    
    // Extract only the model from the title (assuming format like "2022 Honda Civic" or "Honda Civic")
    let modelOnly = 'Untitled';
    if (eventInfo.event.title) {
      const titleParts = eventInfo.event.title.split(' ');
      // If we have at least 2 parts, assume the last part is the model
      if (titleParts.length >= 2) {
        modelOnly = titleParts[titleParts.length - 1];
      } else {
        modelOnly = eventInfo.event.title;
      }
    }
    
    // Create a wrapper component for long press
    const EventWrapper = ({ children }: { children: React.ReactNode }) => {
      const longPressHandlers = useLongPress({
        onLongPress: () => handleLongPress(eventInfo.event.id),
        onClick: () => handleEventClick({ event: eventInfo.event }),
        threshold: 600,
        preventDefault: false // Allow natural scrolling
      });

      return (
        <div {...longPressHandlers} className="h-full w-full cursor-pointer">
          {children}
        </div>
      );
    };
    
    return (
      <EventWrapper>
        <div className="p-1 relative overflow-hidden">
        {shouldShowSecondaryColor && (
          <div 
            className="secondary-color-line absolute left-0 right-0" 
            style={{ 
              backgroundColor: secondaryColor,
              height: '20px',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 2,
              marginLeft: '-5px',
              marginRight: '-5px',
              boxShadow: '0 0 4px rgba(0,0,0,0.2)',
              borderRadius: '2px'
            }}
          />
        )}
        
        {/* Workflow Progress */}
        {showWorkflowProgress && (
          <div 
            className="absolute top-0 right-0 z-30 flex items-center px-1 py-0.5 rounded-bl-md"
            style={{
              backgroundColor: 'rgba(234, 179, 8, 0.95)', // Match in_progress color (#eab308)
              fontSize: isMobile ? '9px' : '11px',
              fontWeight: '600'
            }}
          >
            <span className="text-white">
              {completedSteps}/{totalSteps}
            </span>
          </div>
        )}
        
        {/* Feedback Rating for completed bookings */}
        {showFeedbackRating && (
          <div 
            className="absolute z-30 flex items-center px-1 py-0.5 rounded-bl-md"
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              fontSize: isMobile ? '8px' : '10px',
              top: showWorkflowProgress ? (isMobile ? '14px' : '16px') : 0,
              right: 0
            }}
          >
            <Star 
              size={isMobile ? 10 : 12} 
              className="text-yellow-400 fill-current mr-0.5" 
            />
            <span className="text-gray-800 font-medium">
              {feedbackData.rating.toFixed(1)}
            </span>
          </div>
        )}
        
        {/* Payment Status Stamp */}
        {paymentStatus && (
          <div 
            className={`absolute bottom-0 right-0 z-20 px-0.5 py-0 text-[5px] sm:text-[8px] font-bold rounded-tl-sm ${paymentStampColor}`}
            style={{ 
              fontSize: isMobile ? '5px' : '8px',
              lineHeight: '1',
              letterSpacing: '0.02em',
              padding: isMobile ? '1px 2px' : '2px 4px'
            }}
          >
            {paymentStatus}
          </div>
        )}
        
        {/* Warranty Indicator */}
        {eventInfo.event.extendedProps.is_warranty && (
          <div 
            className="absolute top-0 left-0 z-20 px-0.5 py-0 text-[5px] sm:text-[8px] font-bold rounded-br-sm text-white"
            style={{ 
              fontSize: isMobile ? '5px' : '8px',
              lineHeight: '1',
              letterSpacing: '0.02em',
              padding: isMobile ? '1px 2px' : '2px 4px',
              backgroundColor: 'rgba(107, 114, 128, 0.9)' // Grey background
            }}
          >
            🛡️
          </div>
        )}

        {/* Active Timer Display */}
        {hasActiveTimer && (
          <div 
            className="absolute bottom-0 left-0 right-0 z-30 flex items-center justify-center px-1 py-1"
            style={{
              backgroundColor: 'rgba(220, 38, 38, 0.95)', // Red background
              borderTop: '1px solid rgba(255, 255, 255, 0.3)'
            }}
          >
            <div className="text-white font-mono font-bold animate-pulse" style={{ fontSize: isMobile ? '10px' : '12px' }}>
              {formatTimerDisplay(timerElapsedTime)}
            </div>
          </div>
        )}
        
        {/* Display booking number and customer name on first line */}
        <div className="flex items-center space-x-1 relative z-10">
          {bookingNumber && (
            <div className="text-[9px] sm:text-xs font-medium">{bookingNumber}</div>
          )}
          {isLoaded && eventInfo.event.extendedProps.customer && (
            <div className="text-[9px] sm:text-xs font-medium">- {eventInfo.event.extendedProps.customer}</div>
          )}
        </div>
        
        {/* Display only vehicle model on second line */}
        <div className="text-[9px] sm:text-sm truncate relative z-10 mt-0.5">{modelOnly}</div>
        
        {/* Display time on third line */}
        <div className="text-[8px] sm:text-xs opacity-75 truncate relative z-10 mt-0.5">{formattedTime}</div>
        
        {!isLoaded && (
          <div className="text-[8px] sm:text-xs opacity-50 relative z-10">Loading...</div>
        )}
      </div>
      </EventWrapper>
    );
  }, [loadedBookingIds, isMobile, bookingFeedback, handleLongPress, handleEventClick, activeTimer, timerElapsedTime]);
  
  // Force a calendar redraw/rerender when bookings change
  useEffect(() => {
    if (calendarRef.current && bookings.length > 0) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.refetchEvents();
    }
  }, [bookings, bookingFeedback]);
  
  // Force a redraw when the loading state changes to ensure colors are applied
  useEffect(() => {
    if (!isLoading && calendarRef.current && bookings.length > 0) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.updateSize();
    }
  }, [isLoading, bookings]);
  
  // Memoize calendar events to prevent unnecessary re-renders - combine bookings and time off
  const calendarEvents = useMemo(() => [...bookings, ...timeOffRequests], [bookings, timeOffRequests]);
  
  // Add a dayCellContent renderer for the day number
  const renderDayCellContent = (arg: any) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
        <span className={arg.isToday ? 'calendar-today-pill' : ''}>{arg.dayNumberText}</span>
      </div>
    );
  };
  
  // Format currency for display
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };
  
  // Update isMobile state on window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Simple CSS-based sidebar detection - much more reliable
  useEffect(() => {
    // Set CSS custom properties that the header can use
    const updateHeaderLayout = () => {
      const mainContentParent = document.querySelector('.main-content')?.parentElement as HTMLElement;
      if (mainContentParent) {
        const computedStyle = window.getComputedStyle(mainContentParent);
        const marginLeft = computedStyle.marginLeft;
        const width = computedStyle.width;
        
        // Set CSS variables that the header can use
        document.documentElement.style.setProperty('--calendar-header-left', marginLeft);
        document.documentElement.style.setProperty('--calendar-header-width', width);
      }
    };

    // Update initially
    updateHeaderLayout();

    // Use ResizeObserver for reliable detection
    const resizeObserver = new ResizeObserver(updateHeaderLayout);
    const mainContentParent = document.querySelector('.main-content')?.parentElement;
    
    if (mainContentParent) {
      resizeObserver.observe(mainContentParent);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  // Fix for mobile calendar height issue
  useEffect(() => {
    if (!isMobile || !calendarRef.current) return;
    
    // Function to fix calendar height on mobile
    const fixCalendarHeight = () => {
      try {
        // Target the key elements that might be causing height restrictions
        const viewHarness = document.querySelector('.fc-view-harness');
        const daygridBody = document.querySelector('.fc-daygrid-body');
        const scrollGrid = document.querySelector('.fc-scrollgrid');
        const scrollers = document.querySelectorAll('.fc-scroller');
        const container = document.querySelector('.calendar-v2-container');
        
        // Remove any height restrictions
        if (viewHarness) {
          (viewHarness as HTMLElement).style.height = 'auto';
          (viewHarness as HTMLElement).style.minHeight = '1200px'; // Much taller
        }
        
        if (daygridBody) {
          (daygridBody as HTMLElement).style.height = 'auto';
        }
        
        if (scrollGrid) {
          (scrollGrid as HTMLElement).style.height = 'auto';
        }
        
        // Fix all scrollers
        scrollers.forEach(scroller => {
          (scroller as HTMLElement).style.height = 'auto';
          (scroller as HTMLElement).style.overflow = 'visible';
        });
        
        // Add extra padding to container
        if (container) {
          (container as HTMLElement).style.paddingBottom = '800px';
        }
        
        // Force a redraw of the calendar if calendar reference exists
        if (calendarRef.current) {
          const calendarApi = calendarRef.current.getApi();
          calendarApi.updateSize();
        }
      } catch (error) {
        console.error('Error fixing calendar height:', error);
      }
    };
    
    // Apply fix after calendar is rendered
    fixCalendarHeight();
    
    // Apply fix whenever window is resized
    window.addEventListener('resize', fixCalendarHeight);
    
    // Set interval to keep checking and fixing (helps with iOS Safari)
    const intervalId = setInterval(fixCalendarHeight, 1000);
    
    return () => {
      window.removeEventListener('resize', fixCalendarHeight);
      clearInterval(intervalId);
    };
  }, [isMobile]);
  
  // Add a function to load feedback for completed bookings
  const loadFeedbackForBookings = useCallback(async (bookings: CalendarBooking[]) => {
    if (!selectedBusiness?.id) return;
    
    const completedBookings = bookings.filter(booking => booking.extendedProps.status === 'completed');
    if (completedBookings.length === 0) return;
    
    const feedbackMap = new Map<string, { rating: number; hasText: boolean }>();
    
    // Load feedback for each completed booking
    await Promise.all(
      completedBookings.map(async (booking) => {
        try {
          const feedback = await getBookingFeedback(booking.id);
          if (feedback) {
            // Calculate average rating from form_responses or legacy fields
            let averageRating = 0;
            let ratingCount = 0;
            
            if (feedback.form_responses && Object.keys(feedback.form_responses).length > 0) {
              // Use dynamic form responses
              Object.values(feedback.form_responses).forEach(value => {
                if (typeof value === 'number' && value > 0) {
                  averageRating += value;
                  ratingCount++;
                }
              });
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            } else {
              // Fallback to legacy fields
              if (feedback.overall_quality_rating) {
                averageRating += feedback.overall_quality_rating;
                ratingCount++;
              }
              if (feedback.overall_experience_rating) {
                averageRating += feedback.overall_experience_rating;
                ratingCount++;
              }
              if (ratingCount > 0) {
                averageRating = averageRating / ratingCount;
              }
            }
            
            if (averageRating > 0) {
              feedbackMap.set(booking.id, {
                rating: averageRating,
                hasText: !!(feedback.improvement_suggestions || 
                          (feedback.form_responses && Object.values(feedback.form_responses).some(v => typeof v === 'string' && v.trim())))
              });
            }
          }
        } catch (error) {
          console.error('Error loading feedback for booking:', booking.id, error);
        }
      })
    );
    
    setBookingFeedback(feedbackMap);
  }, [selectedBusiness?.id]);
  
  // Calculate if we should start with today's date centered
  const getInitialDate = useCallback(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    
    // Always start with today's date so calendar centers on current week
    return today;
  }, []);

  // Position calendar to today's date immediately after render, before showing
  useEffect(() => {
    if (calendarRef.current && bookings.length > 0 && !hasInitialScrolled.current && !calendarLoaded) {
      // Delay briefly to let FullCalendar fully render its DOM
      setTimeout(() => {
        const today = new Date();
        const todayString = today.toISOString().split('T')[0]; // YYYY-MM-DD format
        const todayCell = document.querySelector(`[data-date="${todayString}"]`) as HTMLElement;
        
        if (todayCell) {
          // Position to today's date instantly
          todayCell.scrollIntoView({
            behavior: 'auto', // No animation
            block: 'center',
            inline: 'center'
          });
          hasInitialScrolled.current = true;
        }
        
        // Always show the calendar after positioning attempt
        setCalendarLoaded(true);
      }, 100);
      
      // Fallback timeout - show calendar even if positioning fails
      setTimeout(() => {
        if (!calendarLoaded) {
          setCalendarLoaded(true);
        }
      }, 500);
    }
  }, [calendarRef.current, bookings.length, calendarLoaded]);
  
  // Reset calendar loaded state when month changes (for navigation)
  useEffect(() => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const currentYear = today.getFullYear();
    const calendarMonth = currentDate.getMonth();
    const calendarYear = currentDate.getFullYear();
    
    // If we're not on today's month, show calendar immediately
    if (currentMonth !== calendarMonth || currentYear !== calendarYear) {
      setCalendarLoaded(true);
    } else if (!hasInitialScrolled.current) {
      // If we're back to today's month and haven't scrolled yet, hide calendar for positioning
      setCalendarLoaded(false);
    }
  }, [currentDate]);

  return (
    <div className="calendar-v2-container flex flex-col bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/50 min-h-screen" 
         style={{ 
           minHeight: isMobile ? 'auto' : '100vh',
           scrollBehavior: 'smooth', // Add smooth scrolling behavior
           WebkitOverflowScrolling: 'touch', // Improve scrolling on iOS
           fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
           touchAction: 'manipulation',
           WebkitTouchCallout: 'none',
           WebkitUserSelect: 'none'
         }}
    >
      {/* Show loading state while business context is loading */}
      {businessLoading && (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading your calendar...</p>
          </div>
        </div>
      )}
      
      {/* Only show calendar content when business context is loaded */}
      {!businessLoading && (
        <>
      <style jsx global>{`
        /* Fixed header styles */
        .calendar-v2-container {
          position: relative;
        }
        
        /* Calendar fixed header positioning - uses CSS variables set by JS */
        .calendar-fixed-header {
          left: var(--calendar-header-left, 0) !important;
          width: var(--calendar-header-width, 100%) !important;
        }
        
        /* Ensure fixed header is above all calendar content */
        .calendar-v2-container .bg-gray-800.fixed {
          z-index: 1000 !important;
        }
        
        /* Calendar spacing */
        .calendar-v2-container .fc {
          margin-top: 8px;
        }
        
        /* Override any sticky behavior from FullCalendar */
        .fc-header-toolbar,
        .fc .fc-toolbar,
        .fc-header-toolbar.fc-toolbar,
        .fc-sticky,
        .fc-sticky-header {
          position: static !important;
          z-index: 1 !important;
          top: auto !important;
        }
        
        /* Ensure the weekday headers aren't sticky */
        .fc-col-header,
        .fc-col-header-cell,
        .fc thead,
        .fc-scroller-harness,
        .fc .fc-scrollgrid-section,
        .fc .fc-scrollgrid-section-header,
        .fc .fc-scrollgrid-section-header > *,
        .fc .fc-scrollgrid-section-sticky {
          position: static !important;
          z-index: 1 !important;
          top: auto !important;
        }
        
        /* Also fix any fixed table elements */
        table.fc-scrollgrid.fc-scrollgrid-liquid,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead tr,
        table.fc-scrollgrid.fc-scrollgrid-liquid thead th {
          position: static !important;
          top: auto !important;
        }
        
        /* Mobile optimizations for space */
        @media (max-width: 640px) {
          .fc .fc-toolbar.fc-header-toolbar {
            margin-bottom: 0.5em;
          }
          
          .fc .fc-toolbar-title {
            font-size: 1.2em;
          }
          
          /* Reduce day header height */
          .fc-theme-standard th {
            padding: 4px 0 !important;
          }
          
          /* Smaller day numbers */
          .fc .fc-daygrid-day-top {
            padding: 2px 4px !important;
          }
          
          /* Optimize booking blocks for mobile */
          .fc-daygrid-event {
            padding: 1px 2px !important;
            margin-bottom: 1px !important;
          }
          
          /* Reduce space between booking blocks */
          .fc-daygrid-day-events {
            padding: 0 1px !important;
            margin: 0 !important;
          }
          
          /* Ensure day cells can expand as needed to fit all events */
          .fc .fc-daygrid-day-frame {
            min-height: auto !important;
            height: auto !important;
            padding: 2px 1px !important;
          }
          
          /* Remove any "more" link styling as it should no longer appear */
          .fc-daygrid-more-link {
            display: none !important;
          }
          
          /* Ensure day bottom padding doesn't cause any cut off */
          .fc-daygrid-day-bottom {
            padding: 0 !important;
          }
          
          /* Specific optimizations for iOS Safari */
          @supports (-webkit-touch-callout: none) {
            .fc .fc-daygrid-day-frame {
              padding-top: 2px !important;
            }
          }
          
          /* Make calendar stretch to edges on mobile */
          .fc {
            width: 100% !important; /* Use 100% instead of viewport width */
            max-width: 100% !important; /* Use 100% instead of viewport width */
            margin-left: 0 !important; /* Remove negative margin */
            margin-right: 0 !important;
            padding-left: 0 !important; /* Ensure no extra padding */
            padding-right: 0 !important; /* Remove right padding that was compensating for negative margin */
          }
          
          /* Remove excessive padding in calendar cells */
          .fc td, .fc th {
            padding: 0 !important;
          }
          
          /* Additional right edge fix */
          .fc td:last-child, 
          .fc th:last-child {
            border-right: 0 !important;
            padding-right: 0 !important;
          }
          
          /* Fix table width to prevent overflow */
          .fc table {
            width: 100% !important; /* Use 100% instead of calc */
          }
        }
      `}</style>
      
      {/* Fixed Header with navigation controls - positioned within main content area */}
      <div 
        className="bg-gray-800 border-b border-gray-600 shadow-lg fixed z-50 calendar-fixed-header"
        style={{ 
          top: 'calc(3rem + env(safe-area-inset-top, 0px))', // Position below main header
          opacity: isCalendarReady ? 1 : 0,
          transition: isCalendarReady ? 'opacity 0.2s ease-in' : 'none'
        }}
      >
        <div className="flex items-center justify-between px-2 sm:px-6 py-2 sm:py-4 gap-1 sm:gap-4">
          {/* Navigation controls grouped together */}
          <div className="flex items-center space-x-1 sm:space-x-2">
            <button 
              onClick={handlePrev}
              className="bg-gray-700 border border-gray-600 rounded-lg sm:rounded-xl px-1.5 sm:px-2 py-2 sm:p-3 hover:bg-gray-600 transition-all duration-200 flex items-center justify-center shadow-sm active:scale-95"
              disabled={isLoading}
              style={{ minHeight: '32px' }}
            >
              <ChevronLeft className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </button>
            
            <div className="text-sm sm:text-2xl font-bold px-1 sm:px-6 text-white text-center sm:text-left min-w-0">
              <span className="truncate block">{calendarTitle}</span>
            </div>
            
            <button
              onClick={handleNext}
              className="bg-gray-700 border border-gray-600 rounded-lg sm:rounded-xl px-1.5 sm:px-2 py-2 sm:p-3 hover:bg-gray-600 transition-all duration-200 flex items-center justify-center shadow-sm active:scale-95"
              disabled={isLoading}
              style={{ minHeight: '32px' }}
            >
              <ChevronRight className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </button>
          </div>

          {/* Sales summary - always on the same row */}
          {canAccess.calendarSales && (
            <div className="bg-gray-700 border border-gray-600 rounded-lg sm:rounded-xl px-2 sm:px-4 py-1 sm:py-2 shadow-sm flex-shrink-0">
              <span className="text-xs sm:text-xl font-bold text-white whitespace-nowrap">{formatCurrency(totalSales)}</span>
            </div>
          )}
        </div>
      </div>
      
      {error && (
        <div className="backdrop-blur-xl bg-red-50/80 border border-red-200/50 text-red-600 p-3 sm:p-4 mx-2 sm:mx-6 rounded-xl sm:rounded-2xl shadow-lg text-sm sm:text-base"
             style={{ 
               backdropFilter: 'blur(20px)', 
               WebkitBackdropFilter: 'blur(20px)',
               marginTop: isMobile ? 'calc(3rem + 60px + env(safe-area-inset-top, 0px))' : 'calc(3rem + 80px + env(safe-area-inset-top, 0px))' // Position below both headers
             }}>
          {error}
        </div>
      )}

      
              <div className={`flex-grow flex flex-col relative w-full mb-2 sm:mb-6 backdrop-blur-xl bg-white/70 shadow-lg overflow-hidden ${isMobile ? '' : 'rounded-2xl border border-white/20'}`} 
            style={{ 
              minHeight: 'auto',
              height: 'auto',
              position: 'static', // Prevent positioning issues on iOS
              overflowY: 'visible', // Make sure content can expand vertically
              padding: isMobile ? 0 : 0,
              marginTop: isMobile ? 'calc(3rem + 3px + env(safe-area-inset-top, 0px))' : 'calc(3rem + 35px + env(safe-area-inset-top, 0px))', // Account for main header + calendar header
              marginBottom: '1rem',
              marginLeft: 0,
              marginRight: 0,
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)'
            }}>
        <style jsx global>{`
          /* Additional fix for left edge alignment */
          @media (max-width: 640px) {
            /* Remove all padding and margins from calendar container and components */
            .calendar-v2-container > div:last-child {
              padding: 0 !important;
              margin-left: 0 !important;
              margin-right: 0 !important;
            }
            
            /* Target the specific FullCalendar view harness that has the 8px padding */
            .fc-view-harness.fc-view-harness-passive {
              padding: 0 !important;
            }
            
            /* Target the calendar container to ensure it extends to the left edge */
            .fc-view-harness, 
            .fc-view-harness-active,
            .fc-daygrid,
            .fc-daygrid-body,
            .fc-scrollgrid,
            .fc-scrollgrid-liquid {
              padding: 0 !important;
              margin: 0 !important;
              left: 0 !important;
          }
            
            /* Target the calendar table to ensure it starts from the left edge */
            .fc table,
            .fc-scrollgrid-sync-table {
              margin-left: 0 !important;
              padding-left: 0 !important;
              left: 0 !important;
              width: 100% !important;
          }
            
            /* Adjust cell borders to ensure proper edge alignment */
            .fc td:first-child, 
            .fc th:first-child {
              border-left: 0 !important;
            }
            
            /* Fix any potential wrapper div issues */
            .fc-view {
              padding-left: 0 !important;
              margin-left: 0 !important;
          }
          }
          
          /* iOS-specific optimizations */
          /* Improve touch responsiveness */
          * {
            -webkit-tap-highlight-color: rgba(0,0,0,0);
            touch-action: manipulation;
          }
          
          /* Fix for iOS hover states - prevent stuck hover effects */
          @media (hover: none) {
            .fc-event:hover {
              transform: none !important;
              transition: none !important;
          }
            
            .fc a:hover {
              opacity: 1 !important;
          }
          }
          
          /* Improve scroll performance on iOS */
          .fc {
            -webkit-overflow-scrolling: touch;
            -webkit-transform: translateZ(0);
            -webkit-perspective: 1000;
            backface-visibility: hidden;
          }
          
          /* FIX SCROLLING OVER BOOKING BLOCKS */
          /* Allow scroll when touching booking blocks */
          .fc-event,
          .fc-daygrid-event,
          .fc-daygrid-block-event {
            touch-action: pan-y !important; /* Allow vertical scrolling on touch */
            pointer-events: auto !important; /* Still capture clicks but let scrolls through */
            /* Ensure events are tappable but don't block scrolling */
            position: relative;
            z-index: 5;
          }
          
          /* Fix for iOS scroll handling */
          @media (max-width: 768px) {
            /* Make all event blocks have pan-y behavior */
            .fc-daygrid-event-harness {
              touch-action: pan-y !important;
          }
            
            /* Inner parts of the event blocks should let touches pass through for scrolling */
            .fc-event-main,
            .fc-event-title,
            .fc-event-time {
              touch-action: pan-y !important;
          }
            
            /* Ensure container scroll works */
            .fc-view,
            .fc-scroller,
            .fc-scroller-liquid,
            .fc-scrollgrid {
              touch-action: pan-y !important;
          }
          }
          
          /* Prevent text selection on touch */
          .fc-daygrid-day-number,
          .fc-daygrid-day-top,
          .fc-event-title,
          .fc-event-time {
            -webkit-user-select: none;
            user-select: none;
          }
          
          /* Make tappable areas larger for mobile */
          @media (max-width: 768px) {
            .fc-event {
              min-height: 30px;
              padding: 4px;
          }
            
            .fc-daygrid-day-number {
              padding: 8px !important;
          }
            
            /* Increase tap target size for day cells */
            .fc-daygrid-day-frame {
              min-height: 80px !important;
          }
          }
          
          /* AGGRESSIVELY TARGET AND HIDE BACKGROUND ELEMENT */
          /* First, make all parent containers transparent */
          .fc-view-harness,
          .fc-view-harness-active,
          .fc-theme-standard .fc-view-harness {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            overflow: visible !important;
          }
          
          /* Remove all background containers that might have styling */
          .fc .fc-scrollgrid,
          .fc .fc-scrollgrid-liquid,
          .fc .fc-scrollgrid-section,
          .fc-scrollgrid-sync-container,
          .fc-scrollgrid-sync-table,
          .fc .fc-scrollgrid-section-liquid {
            background-color: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            opacity: 1 !important;
          }
          
          /* Main calendar container */
          .fc {
            background: transparent !important;
            border: none !important;
            box-shadow: none !important;
          }
          
          /* This is the border container for the entire table */
          .fc .fc-scrollgrid {
            border: none !important;
            border-radius: 0 !important;
            overflow: visible !important;
          }
          
          /* Create cell borders directly */
          .fc-theme-standard td, 
          .fc-theme-standard th {
            border: 1px solid #e5e7eb !important;
            background-color: white !important;
          }
          
          /* Ensure frame expansion for all cells */
          .fc .fc-daygrid-day-frame {
            height: auto !important;
            min-height: 120px !important;
            background-color: white !important;
          }
          
          /* Force table wrapping container to be transparent */
          .fc-view, 
          .fc-dayGridMonth-view,
          .fc-view-harness, 
          .fc-view-harness-active,
          table.fc-scrollgrid {
            background: transparent !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
          }
          
          /* Reset scrolling container styles */
          .fc, 
          .fc .fc-view,
          .fc .fc-scrollgrid,
          .fc .fc-scrollgrid-section,
          .fc .fc-scrollgrid-section-body,
          .fc .fc-scrollgrid-section-header,
          .fc .fc-scroller,
          .fc .fc-scroller-harness,
          .fc .fc-scroller-liquid,
          .fc .fc-scroller-liquid-absolute,
          .fc .fc-daygrid,
          .fc .fc-daygrid-body,
          .fc .fc-scrollgrid-sync-table {
            overflow: visible !important;
            height: auto !important;
            max-height: none !important;
          }
          
          /* DO NOT TOUCH BOOKING BLOCKS */
          .fc-event,
          .fc-daygrid-event,
          .fc-daygrid-block-event {
            /* Preserve these untouched */
          }
          
          /* Preserve cell styling for day frames */
          .fc-daygrid-day-frame {
            background-color: white !important;
          }
          
          /* Other necessary styles */
          .fc-daygrid-day,
          .fc-daygrid-day-top {
            background-color: white !important;
          }
          
          /* Last container attempt - target absolutely everything */
          div[class*="fc-"] {
            border-radius: 0 !important;
          }
          
          /* Calendar today styling */
          .fc .fc-daygrid-day.fc-day-today {
            background: transparent !important;
          }
          
          .calendar-today-pill {
            background: #3b82f6 !important;
            color: #fff !important;
            border-radius: 9999px;
            padding: 2px 8px;
            font-weight: 600;
            display: inline-block;
          }
          
          /* Fix the scrolling container styles to allow full display */
          .fc .fc-view-harness,
          .fc-view-harness-active,
          .fc-daygrid,
          .fc-daygrid-body,
          .fc-scrollgrid,
          .fc-scrollgrid-liquid,
          .fc-scroller,
          .fc-scroller-harness {
            height: auto !important;
            max-height: none !important;
            overflow: visible !important;
          }
          
          /* Ensure the entire grid can expand */
          .fc-scrollgrid-sync-table {
            height: auto !important;
          }
          
          /* Make each cell able to grow as needed */
          .fc .fc-daygrid-body-balanced .fc-daygrid-day-events {
            position: relative !important;
          }
          
          /* Ensure all events show and are visible */
          .fc-v-event, .fc-h-event, .fc-daygrid-event {
            visibility: visible !important;
            display: block !important;
          }
        `}</style>
        <div 
          className="w-full overflow-visible relative p-2 sm:p-4" 
          style={{ 
            background: 'transparent', 
            touchAction: 'manipulation',
            WebkitOverflowScrolling: 'touch',
            margin: 0,
            padding: 'clamp(6px, 2vw, 16px)',
            opacity: calendarLoaded ? 1 : 0,
            transition: calendarLoaded ? 'opacity 0.1s ease-in' : 'none'
          }}
        >
          <FullCalendar
            ref={calendarRef}
            plugins={[dayGridPlugin]}
            initialView="dayGridMonth"
            initialDate={getInitialDate()}
            headerToolbar={false}
            height="auto"
            contentHeight="auto"
            aspectRatio={isMobile ? 0.8 : 1.7}
            dayMaxEvents={false}
            firstDay={1}
            weekNumbers={false}
            navLinks={false}
            editable={false}
            selectable={false} // Disable selection to improve scrolling
            longPressDelay={100}
            selectLongPressDelay={300}
            dayHeaderFormat={{ weekday: 'short' }}
            stickyHeaderDates={false}
            expandRows={!isMobile}
            eventTimeFormat={{
              hour: 'numeric',
              minute: '2-digit',
              meridiem: 'short'
            }}
            themeSystem="standard"
            slotEventOverlap={false}
            fixedWeekCount={false}
            businessHours={false}
            nowIndicator={true}
            datesSet={handleDatesSet}
            events={calendarEvents}
            eventClick={handleEventClick}
            eventContent={renderEventContent}
            dayCellContent={renderDayCellContent}
            lazyFetching={false}
            forceEventDuration={true}
            handleWindowResize={true}
            rerenderDelay={0}
            eventDisplay="block"
            eventMinHeight={isMobile ? 18 : 25}
            eventMinWidth={10}
            eventShortHeight={isMobile ? 18 : 25}
            eventLongPressDelay={400} // Increase to help distinguish between tap and scroll
            eventOverlap={false}
            eventOrder="extendedProps.booking_number"
            eventConstraint={{
              startTime: '00:00',
              endTime: '24:00',
              dows: [0, 1, 2, 3, 4, 5, 6]
            }}

          />
        </div>
      </div>
      
      {isBookingInfoModalOpen && (
        <BookingInfoModal
          isOpen={isBookingInfoModalOpen}
          onClose={() => {
            console.log('📱 User manually closed modal, clearing localStorage');
            // Removed all persistence systems
            setSelectedBooking(null);
            setIsBookingInfoModalOpen(false);
            setIsLoadingBookingFromNav(false);
          }}
          booking={selectedBooking}
          onUpdate={() => fetchBookings(true)}
          onRescheduleMobileBooking={handleRescheduleMobileBooking}
          readOnly={isLoadingBookingFromNav} // Make read-only while loading
        />
      )}
      
      <BookingQuickInfoModal
        isOpen={isBookingQuickInfoModalOpen}
        onClose={() => {
          setIsBookingQuickInfoModalOpen(false);
          setQuickInfoBooking(null);
        }}
        booking={quickInfoBooking}

      />
      
      <DaySummaryModal
        isOpen={isDaySummaryOpen}
        onClose={() => setIsDaySummaryOpen(false)}
        selectedDate={selectedDay}
        position={daySummaryPosition || { x: 0, y: 0, width: 0, height: 0 }}
        isLocationBooking={true}
        onCreateBooking={() => {}}
        onBookingClick={() => {}}
      />
      
      {isRescheduleMobileBookingModalOpen && bookingToReschedule && (
        <CreateMobileBookingModal
          isOpen={isRescheduleMobileBookingModalOpen}
          onClose={() => {
            setIsRescheduleMobileBookingModalOpen(false);
            setBookingToReschedule(null);
          }}
          onSuccess={() => {
            setIsRescheduleMobileBookingModalOpen(false);
            setBookingToReschedule(null);
            fetchBookings(true);
          }}
          isReschedule={true}
          existingBookingId={bookingToReschedule.id}
          prefilledData={{
            customer: bookingToReschedule.customer,
            services: (bookingToReschedule.services || []).map((service: any) => ({
              vehicle: service.vehicle_id ? {
                id: service.vehicle_id,
                make: service.vehicle_make || '',
                model: service.vehicle_model || '',
                year: service.vehicle_year || '',
                color: service.vehicle_color || '',
                customer_id: bookingToReschedule.customer?.id || '',
                business_id: bookingToReschedule.business_id,
                created_at: '',
                updated_at: ''
              } : null,
              service: {
                id: service.service_id,
                name: service.name,
                description: service.description,
                price: service.price,
                duration_minutes: service.duration_minutes,
                business_id: bookingToReschedule.business_id,
                category: '',
                tax_rate: service.tax_rate,
                tax_exempt: service.tax_exempt,
                active: true,
                price_options: [],
                created_at: '',
                updated_at: ''
              },
              priceOption: {
                id: '',
                service_id: service.service_id,
                name: 'Standard',
                price: service.price,
                duration_minutes: service.duration_minutes
              }
            })),
            scheduleData: {
              dropOffDate: bookingToReschedule.drop_off_date || bookingToReschedule.start_date || '',
              dropOffTime: bookingToReschedule.drop_off_time || bookingToReschedule.start_time || '',
              startDate: bookingToReschedule.start_date || '',
              startTime: bookingToReschedule.start_time || '',
              endDate: bookingToReschedule.end_date || '',
              endTime: bookingToReschedule.end_time || '',
              customer_notes: bookingToReschedule.customer_notes || ''
            },
            bookingAddress: bookingToReschedule.booking_address || '',
            bookingColor: bookingToReschedule.color || '#3B82F6',
            bookingSecondaryColor: bookingToReschedule.secondary_color
          }}
        />
      )}
        </>
      )}
    </div>
  );
} 